import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast, Toaster } from "sonner";
import { ArrowLeft, ShieldAlert, ShieldCheck, Users, Briefcase, DollarSign, Loader2, PlusCircle, ClipboardList } from "lucide-react";

const WORKER_TONE = {
  Hired:"bg-emerald-50 text-emerald-800 border-emerald-300",
  Active:"bg-emerald-100 text-emerald-900 border-emerald-400",
  Former:"bg-slate-100 text-slate-600 border-slate-300",
  Alumni:"bg-slate-100 text-slate-600 border-slate-300",
  "Rehire Eligible":"bg-sky-50 text-sky-700 border-sky-300",
};

export default function EmployerPlacements() {
  const nav = useNavigate();
  const [journey, setJourney] = useState(null);
  const [replacing, setReplacing] = useState(null);
  const [replaceForm, setReplaceForm] = useState({reason:"", reported_issue_type:"OTHER", details:""});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const {data} = await api.get("/employer/journey"); setJourney(data); }
    catch (e) { toast.error("Failed to load journey"); }
  };
  useEffect(() => { load(); }, []);

  const requestReplacement = async () => {
    if (!replacing) return;
    setBusy(true);
    try {
      await api.post(`/placements/${replacing.id}/guarantee/replacement-request`, {
        placement_id: replacing.id, ...replaceForm });
      toast.success("Replacement request submitted — ConnectMyHub will review");
      setReplacing(null); setReplaceForm({reason:"", reported_issue_type:"OTHER", details:""});
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Request failed"); }
    finally { setBusy(false); }
  };

  if (!journey) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-slate-100 px-6 py-3 flex items-center gap-3">
        <button onClick={()=>nav("/employer/dashboard")} data-testid="ep-back" className="text-xs inline-flex items-center gap-1"><ArrowLeft size={12}/> Back</button>
        <div className="font-display font-bold text-sm">Placements & Workers</div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-8">
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Wave 4</div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Your placements, workers & guarantees</h1>
        </div>

        <section className="mb-8">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Briefcase size={16}/> Placements ({journey.placements.length})</h2>
          {journey.placements.length === 0 ? <div className="p-8 text-center text-sm text-slate-500 bg-white border border-slate-200 rounded-xl">No placements yet — confirm an accepted offer to create one.</div> :
            <div className="space-y-3">{journey.placements.map(p => (
              <div key={p.id} data-testid={`placement-${p.id}`} className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">{p.plan_name_snapshot || p.plan_code_snapshot} plan · {p.status}</div>
                    <div className="font-display text-lg font-bold text-slate-900">{p.role_title || "—"}</div>
                    <div className="text-xs text-slate-500 mt-1">Start {p.start_date} · {p.service_model} · Guarantee {p.guarantee_days_snapshot}d</div>
                  </div>
                  <button data-testid={`replace-${p.id}`} onClick={()=>setReplacing(p)} className="h-9 px-3 rounded border border-rose-300 bg-rose-50 text-rose-700 text-xs font-semibold inline-flex items-center gap-1"><ShieldAlert size={12}/> Request replacement</button>
                </div>
                <div className="mt-4 grid md:grid-cols-2 gap-3">
                  <div className="border border-slate-200 rounded-lg p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">50/50 Milestones (billed via 1Hub/GHL — not processed here)</div>
                    <div className="space-y-1">
                      {(p.milestones||[]).map(m => (
                        <div key={m.code} data-testid={`ms-${p.id}-${m.code}`} className="flex justify-between text-xs">
                          <span className="font-semibold text-slate-700">{m.code} <span className="text-slate-400">({m.pct}%)</span></span>
                          <span>{m.amount?.toLocaleString()} {p.compensation_currency} · <span className={m.status==="PAID"?"text-emerald-700 font-semibold":m.status==="BILLED"?"text-sky-700":"text-slate-600"}>{m.status}</span></span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="border border-slate-200 rounded-lg p-3">
                    <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Fee summary</div>
                    <div className="text-xs space-y-0.5">
                      <div>Fee: <b>{p.placement_fee?.toLocaleString()} {p.compensation_currency}</b></div>
                      <div>Deposit: {p.deposit_amount?.toLocaleString()} · Balance: {p.balance_amount?.toLocaleString()}</div>
                      <div>Paid so far: {p.amount_paid?.toLocaleString()} · Due: {p.balance_due?.toLocaleString()}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}</div>
          }
        </section>

        <section className="mb-8">
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><Users size={16}/> Workers ({journey.workers.length})</h2>
          {journey.workers.length === 0 ? <div className="p-8 text-center text-sm text-slate-500 bg-white border border-slate-200 rounded-xl">No workers yet.</div> :
            <div className="grid md:grid-cols-2 gap-3">{journey.workers.map(w => (
              <div key={w.id} data-testid={`worker-${w.id}`} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex justify-between mb-2">
                  <div className="font-semibold text-slate-900 text-sm">{w.role_title}</div>
                  <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border ${WORKER_TONE[w.status]||"bg-slate-100"}`}>{w.status}</span>
                </div>
                <div className="text-xs text-slate-500">Candidate {w.candidate_id?.slice(0,8)} · Started {w.start_date}</div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-[10px]">
                  <span className="px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700">Training: {w.training_status}</span>
                  <span className="px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700">Cert: {w.certification_status}</span>
                  <span className="px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700">Guarantee: {w.guarantee_status}</span>
                </div>
              </div>
            ))}</div>
          }
        </section>

        <section>
          <h2 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><ShieldAlert size={16}/> Replacement requests ({journey.replacement_requests.length})</h2>
          {journey.replacement_requests.length === 0 ? <div className="p-6 text-center text-xs text-slate-500 bg-white border border-slate-200 rounded-xl">No replacement requests.</div> :
            <div className="space-y-2">{journey.replacement_requests.map(r => (
              <div key={r.id} data-testid={`rr-${r.id}`} className="bg-white border border-slate-200 rounded-xl p-4 text-sm flex justify-between items-center">
                <div>
                  <div className="font-semibold text-slate-900 text-xs">{r.reason}</div>
                  <div className="text-[10px] text-slate-500">{r.reported_issue_type} · {new Date(r.requested_at).toLocaleDateString()}</div>
                </div>
                <span className={`text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border ${r.status==="APPROVED"?"bg-emerald-50 text-emerald-700 border-emerald-300":r.status==="DENIED"?"bg-rose-50 text-rose-700 border-rose-300":r.status==="NEEDS_INFORMATION"?"bg-amber-50 text-amber-800 border-amber-300":"bg-slate-100 text-slate-600 border-slate-200"}`}>{r.status}</span>
              </div>
            ))}</div>
          }
        </section>
      </main>

      {replacing && (
        <div className="fixed inset-0 bg-slate-900/80 z-50 overflow-auto p-4 flex items-start justify-center" onClick={()=>setReplacing(null)}>
          <div data-testid="rr-modal" className="max-w-lg w-full bg-white rounded-xl p-6 mt-8" onClick={e=>e.stopPropagation()}>
            <h2 className="font-display text-2xl font-bold text-slate-900 mb-2">Request replacement</h2>
            <p className="text-sm text-slate-500 mb-4">A ConnectMyHub admin will review and respond. Guarantee-window replacements are covered by your plan; outcomes are: Approved / Denied / Needs Information.</p>
            <div className="space-y-3">
              <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Issue type</label>
                <select data-testid="rr-type" value={replaceForm.reported_issue_type} onChange={e=>setReplaceForm(f=>({...f, reported_issue_type: e.target.value}))} className="w-full h-10 px-3 rounded border border-slate-300 text-sm">{["PERFORMANCE","ATTENDANCE","MISCONDUCT","RESIGNED","MUTUAL","OTHER"].map(x=><option key={x}>{x}</option>)}</select></div>
              <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Reason (short)</label><input data-testid="rr-reason" value={replaceForm.reason} onChange={e=>setReplaceForm(f=>({...f, reason: e.target.value}))} className="w-full h-10 px-3 rounded border border-slate-300 text-sm"/></div>
              <div><label className="block text-[10px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Details</label><textarea data-testid="rr-details" rows={3} value={replaceForm.details} onChange={e=>setReplaceForm(f=>({...f, details: e.target.value}))} className="w-full px-3 py-2 rounded border border-slate-300 text-sm resize-none"/></div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={()=>setReplacing(null)} className="h-10 px-4 rounded border border-slate-300 text-slate-700 text-sm font-semibold">Cancel</button>
              <button data-testid="rr-submit" onClick={requestReplacement} disabled={busy||!replaceForm.reason} className="h-10 px-4 rounded bg-rose-600 text-white text-sm font-semibold disabled:opacity-50">{busy&&<Loader2 size={13} className="animate-spin inline"/>} Submit</button>
            </div>
          </div>
        </div>
      )}
      <Toaster position="top-right" richColors/>
    </div>
  );
}
